import { useCallback, useState, type FC } from "react";
import {
  ActionIcon,
  ActionIconGroup,
  Group,
  Stack,
  Title,
} from "@mantine/core";
import { Carousel } from "@mantine/carousel";
import { IconArrowNarrowLeft, IconArrowNarrowRight } from "@tabler/icons-react";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";
import type { EmblaCarouselType } from "embla-carousel";
import { LearnByWatchingCard } from "./LearnByWatchingCard.js";

const videoTitles = defineMessages({
  csvToMap: {
    id: "UdBuY4Ggy1A",
    defaultMessage: "CSV to Map",
  },
  stylingMap: {
    id: "title.stylingMap",
    defaultMessage: "Styling map",
  },
  excelToMap: {
    id: "title.mappingExcelWithGeocoding",
    defaultMessage: "Mapping Excel with Geocoding",
  },
  // addLayersTheEasyWay: {
  //   id: "title.videoAddLayersTheEasyWay",
  //   defaultMessage: "Add layers the easy way",
  // },
});

const VIDEOS = [
  { youtubeId: "UdBuY4Ggy1A", title: videoTitles.csvToMap, background: "#FFE7B0" },
  { youtubeId: "0iGO1Y3puZ4", title: videoTitles.stylingMap, background: "#E4D7FF" },
  { youtubeId: "ZD65ClIcZRg", title: videoTitles.excelToMap, background: "#C7E4FF" },
  // { youtubeId: "Bke3ZOeDr38", title: videoTitles.addLayersTheEasyWay, background: "#C9EFD0" },
];

export const LearnByWatching: FC = () => {
  const intl = useIntl();
  const [embla, setEmbla] = useState<EmblaCarouselType | null>(null);

  const handleCarouselNext = useCallback(() => {
    if (!embla) return;
    const indexesOfVisibleSlides = embla.slidesInView();
    const indexOfNextSlide = Math.max(...indexesOfVisibleSlides) + 1;
    embla.scrollTo(indexOfNextSlide);
  }, [embla]);

  const handleCarouselPrevious = useCallback(() => {
    if (!embla) return;
    const indexesOfVisibleSlides = embla.slidesInView();
    const indexOfPreviousSlide = Math.max(Math.min(...indexesOfVisibleSlides) - 1, 0);
    embla.scrollTo(indexOfPreviousSlide);
  }, [embla]);

  const previousLabel = intl.formatMessage({
    defaultMessage: "Previous",
    id: "label.previous",
  });

  const nextLabel = intl.formatMessage({
    defaultMessage: "Next",
    id: "label.next",
  });

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Title order={3}>
          <FormattedMessage defaultMessage="Learn by watching" id="title.learnByWatching" />
        </Title>
        <ActionIconGroup style={{ gap: 8 }}>
          <ActionIcon onClick={handleCarouselPrevious} size={40} variant="light" aria-label={previousLabel}>
            <IconArrowNarrowLeft size={20} />
          </ActionIcon>
          <ActionIcon onClick={handleCarouselNext} size={40} variant="light" aria-label={nextLabel}>
            <IconArrowNarrowRight size={20} />
          </ActionIcon>
        </ActionIconGroup>
      </Group>
      <Carousel
        slideSize={320}
        slideGap={24}
        withControls={false}
        withIndicators={false}
        getEmblaApi={setEmbla}
        withKeyboardEvents={false}
      >
        {VIDEOS.map((video, i) => (
          <Carousel.Slide key={i} pb={5}>
            <LearnByWatchingCard
              youtubeId={video.youtubeId}
              title={intl.formatMessage(video.title)}
              background={video.background}
            />
          </Carousel.Slide>
        ))}
      </Carousel>
    </Stack>
  );
};
